import { useCallback, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { FilePlus2Icon, Loader2Icon } from 'lucide-react';

import { swalert, ALERT_CONFIG } from '@/libs/sweetalert';
import { toast_error, toast_success } from '@/libs/toast-wrappers';
import { AISearchDoc, deleteAISearchDoc, uploadAISearchDoc } from '@/api/ai-search-docs';
import { useGetAISearchDocs } from '@/hooks/api/use-get-ai-search-docs';
import { formatBytesLocal } from '@/utils/bytes';

import { Section } from '@/components/content-layout/section';
import { SectionHeader } from '@/components/content-layout/section-header';
import { Button } from '@/components/ui/button';
import { Table } from '@/components/ui/table';
import { FetchStates } from '@/components/fetch-states';

export function AISearchAdminPage() {
  const { t } = useTranslation();
  const { docs, loading, refetchDocs } = useGetAISearchDocs();

  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onUploadClick = () => inputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];
    setUploading(true);
    void (async () => {
      try {
        await uploadAISearchDoc(file);
        toast_success(t('ai-search-admin-upload-success'));
        await refetchDocs();
      } catch {
        toast_error(t('ai-search-admin-upload-error'));
      } finally {
        setUploading(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    })();
  };

  const onDelete = useCallback(
    async (row: unknown) => {
      const doc = row as AISearchDoc;

      const result = await swalert.fire({
        ...ALERT_CONFIG,
        title: t('ai-search-admin-delete-confirm-title'),
        html: `<p style="margin-bottom: 1rem;">${t('ai-search-admin-delete-confirm-text').replace('{filename}', `<span style='font-weight:bold'>${doc.file_name}</span>`)}</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: t('yes'),
        cancelButtonText: t('no'),
      });

      if (result.isConfirmed) {
        try {
          await deleteAISearchDoc(doc.openai_file_id);
          toast_success(t('ai-search-admin-delete-success'));
          await refetchDocs();
        } catch {
          toast_error(t('ai-search-admin-delete-error'));
        }
      }
    },
    [t, refetchDocs]
  );

  const headers = useMemo(
    () => [
      { key: 'file_name', label: t('ai-search-admin-filename') },
      {
        key: 'openai_file_id',
        label: t('ai-search-admin-id'),
        type: 'value' as const,
      },
      {
        key: 'upload_date',
        label: t('ai-search-admin-uploaded'),
        type: 'value' as const,
        format: (v: unknown) => (typeof v === 'string' ? new Date(v).toLocaleString() : '-'),
      },
      {
        key: 'file_size',
        label: t('ai-search-admin-size'),
        type: 'value' as const,
        format: (v: unknown) => formatBytesLocal(parseFloat(v as string)),
      },
      {
        key: 'actions',
        label: t('ai-search-admin-actions'),
        type: 'actions' as const,
        delete: (row: unknown) => void onDelete(row),
      },
    ],
    [t, onDelete]
  );

  return (
    <>
      <Section>
        <SectionHeader title={t('ai-search-admin-title')} element="h1">
          <input ref={inputRef} type="file" className="hidden" onChange={handleFileChange} />
          <Button onClick={onUploadClick} disabled={uploading}>
            {uploading ? (
              <Loader2Icon className="size-5 animate-spin" />
            ) : (
              <FilePlus2Icon className="size-5" />
            )}
            {t('ai-search-admin-upload')}
          </Button>
        </SectionHeader>
      </Section>
      <FetchStates loading={loading} refetch={() => void refetchDocs()} empty={!docs.length}>
        <Section className="overflow-auto scrollbar-hide">
          {!docs.length && <p className="text-sm opacity-60 p-4">{t('ai-search-admin-empty')}</p>}
          {!!docs.length && <Table headers={headers} data={docs} />}
        </Section>
      </FetchStates>
    </>
  );
}
